import { inject } from "ui-common/di";
import type { IWritableCreatorCommunitiesState, ICreatorCommunitiesService } from "./types";
import type { GetCommunitiesResult } from "api-contracts/rpc/community";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { SortDirection } from "ui-components/wow-base-table/types";

export class CreatorCommunitiesService implements ICreatorCommunitiesService {
  @inject<IWritableCreatorCommunitiesState>("writableCreatorCommunitiesState")
  private state!: IWritableCreatorCommunitiesState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private isLoaded = false;

  async loadCommunities(): Promise<void> {
    if (this.isLoaded) return;

    this.state.isLoading.set(true);
    this.state.errorMessage.set("");

    try {
      const result = await this.rpcService.call<GetCommunitiesResult>("get-communities", {});
      this.state.communities.set(result.communities);
      this.state.counts.set(result.counts);
      this.isLoaded = true;
    } catch (error) {
      this.state.errorMessage.set((error as Error).message);
    } finally {
      this.state.isLoading.set(false);
    }
  }

  async refetch(): Promise<void> {
    if (!this.isLoaded) return;

    try {
      const result = await this.rpcService.call<GetCommunitiesResult>("get-communities", {});
      this.state.communities.set(result.communities);
      this.state.counts.set(result.counts);
    } catch (error) {
      console.error("[CreatorCommunities] Refetch failed:", error);
    }
  }

  clearCache(): void {
    this.state.communities.set([]);
    this.state.errorMessage.set("");
    this.state.activeSorts.set([]);
    this.state.counts.set({ draft: 0, published: 0, archived: 0, total: 0 });
    this.isLoaded = false;
  }

  updateSort(column: string, direction: SortDirection): void {
    if (direction === "none") {
      this.state.activeSorts.set(this.state.activeSorts.get().filter((s) => s.column !== column));
    } else {
      const current = this.state.activeSorts.get();
      const existing = current.findIndex((s) => s.column === column);
      if (existing >= 0) {
        const updated = [...current];
        updated[existing] = { column, direction };
        this.state.activeSorts.set(updated);
      } else {
        this.state.activeSorts.set([...current, { column, direction }]);
      }
    }
  }

  async deleteCommunity(id: string): Promise<void> {
    await this.rpcService.call("delete-community", { community_id: id });
    await this.refetch();
  }

  async archiveCommunity(id: string): Promise<void> {
    await this.rpcService.call("archive-community", { community_id: id });
    await this.refetch();
  }

  async reactivateCommunity(id: string): Promise<void> {
    await this.rpcService.call("reactivate-community", { community_id: id });
    await this.refetch();
  }

  async duplicateCommunity(id: string): Promise<void> {
    await this.rpcService.call("duplicate-community", { community_id: id });
    await this.refetch();
  }

  async reorderCommunities(communityIds: string[]): Promise<void> {
    await this.rpcService.call("update-communities-order", { communityIds });
    await this.refetch();
  }
}
